/**
 * StripePaymentForm Component
 *
 * Shared Stripe PaymentElement form with billing details collection.
 * Used by both PaymentMethodSection (inline) and UpdatePaymentMethodModal (modal).
 * Supports credit cards and ACH bank accounts with auto billing details.
 */

import { forwardRef, useImperativeHandle, useState } from 'react';
import { Lock } from '@mui/icons-material';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import type { BillingDetails } from '@store/generated/subscriptions';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripePaymentElementChangeEvent } from '@stripe/stripe-js';

export interface StripePaymentFormHandle {
  /** Trigger payment confirmation programmatically */
  confirmPayment: () => Promise<string>;
  /** Check if form is ready for submission */
  isReady: boolean;
  /** Check if form is currently processing */
  isProcessing: boolean;
}

export interface StripePaymentFormProps {
  /** Callback when payment method is successfully confirmed */
  onPaymentMethodConfirmed?: (paymentMethodId: string) => void;
  /** Callback when an error occurs during payment confirmation */
  onError?: (error: string) => void;
  /** Callback when PaymentElement iframe finishes loading */
  onElementLoaded?: () => void;
  /** Callback when payment form ready state changes (element loaded AND form complete) */
  onReadyChange?: (isReady: boolean) => void;
  /** Callback when micro-deposit verification is required */
  onVerificationPending?: () => void;
  /** Optional existing billing details to pre-fill form */
  existingBillingDetails?: BillingDetails | null;
  /** Whether the parent is processing (to disable form) */
  isParentProcessing?: boolean;
  /** Return URL for Stripe redirect (if required) */
  returnUrl?: string;
  /** Show info alert about payment method requirement */
  showInfoAlert?: boolean;
  /** Show processing indicator */
  showProcessingIndicator?: boolean;
  /** Custom info alert text */
  infoAlertText?: string;
}

/**
 * Shared payment form component that handles Stripe PaymentElement
 * with billing details collection.
 *
 * Use forwardRef to expose confirmPayment to parent components.
 */
export const StripePaymentForm = forwardRef<StripePaymentFormHandle, StripePaymentFormProps>(
  function StripePaymentForm(
    {
      onPaymentMethodConfirmed,
      onError,
      onElementLoaded,
      onReadyChange,
      onVerificationPending,
      existingBillingDetails,
      isParentProcessing = false,
      returnUrl = `${window.location.origin}/practice/subscriptions`,
      showInfoAlert = false,
      showProcessingIndicator = true,
      infoAlertText = 'A payment method is required. You can choose between credit card or bank account (ACH).',
    },
    ref,
  ) {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [isElementReady, setIsElementReady] = useState(false);
    const [processing, setProcessing] = useState(false);

    const isFormReady = isElementReady && isComplete && !processing && !isParentProcessing;

    const handlePaymentElementReady = () => {
      setIsElementReady(true);
      onElementLoaded?.();
    };

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
      setIsComplete(event.complete);
      // Notify parent of overall readiness
      onReadyChange?.(isElementReady && event.complete);
    };

    /**
     * Confirm the payment method with Stripe
     * Returns the payment method ID on success, throws on error
     */
    const confirmPayment = async (): Promise<string> => {
      if (!stripe || !elements) {
        const errorMsg = 'Stripe has not loaded yet. Please wait and try again.';
        setError(errorMsg);
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }

      setProcessing(true);
      setError(null);

      try {
        // Step 1: Trigger form validation (Stripe best practice)
        const { error: submitError } = await elements.submit();

        if (submitError) {
          const errorMsg = submitError.message || 'Please check your payment details and try again.';
          setError(errorMsg);
          setProcessing(false);
          onError?.(errorMsg);
          throw new Error(errorMsg);
        }

        // Step 2: Confirm the setup intent with Stripe
        const { setupIntent, error: setupError } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: 'if_required',
        });

        if (setupError) {
          const errorMsg = setupError.message || 'An error occurred while processing your payment method';
          setError(errorMsg);
          setProcessing(false);
          onError?.(errorMsg);
          throw new Error(errorMsg);
        }

        if (!setupIntent?.payment_method) {
          const errorMsg = 'Failed to process payment method';
          setError(errorMsg);
          setProcessing(false);
          onError?.(errorMsg);
          throw new Error(errorMsg);
        }

        // Check if bank account requires micro-deposit verification
        if (
          setupIntent.status === 'requires_action' &&
          setupIntent.next_action?.type === 'verify_with_microdeposits'
        ) {
          const errorMsg =
            'Bank account verification required. Please check your email for micro-deposit instructions.';
          setProcessing(false);
          onVerificationPending?.();
          // If no verification handler, show error
          if (!onVerificationPending) {
            setError(errorMsg);
            onError?.(errorMsg);
          }
          throw new Error(errorMsg);
        }

        // For any other pending status, don't proceed
        if (setupIntent.status !== 'succeeded') {
          const errorMsg =
            'Your payment method requires additional verification. Please check your email for next steps.';
          setError(errorMsg);
          setProcessing(false);
          onError?.(errorMsg);
          throw new Error(errorMsg);
        }

        // Success! Return payment method ID
        const paymentMethodId = setupIntent.payment_method as string;
        onPaymentMethodConfirmed?.(paymentMethodId);
        return paymentMethodId;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
        if (!error) {
          // Only set if not already set by above handlers
          setError(errorMessage);
        }
        setProcessing(false);
        throw err;
      }
    };

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        confirmPayment,
        isReady: isFormReady,
        isProcessing: processing,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [stripe, elements, isFormReady, processing],
    );

    return (
      <Box>
        {/* Payment Element Container */}
        <Box sx={{ mb: 2 }}>
          <PaymentElement
            onReady={handlePaymentElementReady}
            onChange={handlePaymentElementChange}
            options={{
              layout: 'tabs',
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: 'auto',
                },
              },
              defaultValues: existingBillingDetails
                ? {
                    billingDetails: {
                      name: existingBillingDetails.name || undefined,
                      email: existingBillingDetails.email || undefined,
                      phone: existingBillingDetails.phone || undefined,
                      address: existingBillingDetails.address
                        ? {
                            line1: existingBillingDetails.address.line1 || undefined,
                            line2: existingBillingDetails.address.line2 || undefined,
                            city: existingBillingDetails.address.city || undefined,
                            state: existingBillingDetails.address.state || undefined,
                            postal_code: existingBillingDetails.address.postal_code || undefined,
                            country: existingBillingDetails.address.country || undefined,
                          }
                        : undefined,
                    },
                  }
                : undefined,
            }}
          />
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Optional Info Alert */}
        {showInfoAlert && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">{infoAlertText}</Typography>
          </Alert>
        )}

        {/* Security Note */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            mb: showProcessingIndicator ? 2 : 0,
          }}
        >
          <Lock sx={{ fontSize: 16, mr: 0.5 }} />
          <Typography variant="caption">
            Your payment information is secured and encrypted by Stripe
          </Typography>
        </Box>

        {/* Processing Indicator */}
        {showProcessingIndicator && (processing || isParentProcessing) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {processing ? 'Confirming payment method...' : 'Processing...'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

export default StripePaymentForm;
